// React error boundary. It has to be a CLASS: `getDerivedStateFromError` and `componentDidCatch`
// have no hook equivalent (React 19 included).
//
// No new dependency (react-error-boundary and friends add little over 40 lines) and two intended
// uses: one around the app and one around the MAP ONLY, so that a map failure (deck.gl, tiles, a
// layer with odd data) does not take down the form, which is where the user has half-finished work.
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorFallback } from './fallback'
import { reportError, type ErrorScope } from './report'

interface Props {
  children: ReactNode
  /** Which subtree this boundary covers. Travels to the log so we know WHAT fell. */
  scope: Extract<ErrorScope, 'app' | 'map'>
  /** `true` when it covers a piece of the page (changes the fallback copy). */
  embedded?: boolean
  /** Replaces the default fallback. Receives the error and a function to retry. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(this.props.scope, error, {
      componentStack: info.componentStack,
      url: window.location.href,
    })
  }

  /**
   * Renders the subtree again. If the error is deterministic it lands in this same boundary (and is
   * reported again), which is the honest behaviour: the failure is not swept under the rug.
   */
  reset = () => this.setState({ error: null })

  override render() {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    return (
      <ErrorFallback
        error={error}
        onReset={this.reset}
        embedded={this.props.embedded}
      />
    )
  }
}
